import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  faEye,
  faSync,
  faPhone,
  faRobot,
  faRepeat,
  faMessage,
  faEnvelope,
  faDesktopAlt,
  faCalendarCheck,
  faArrowRotateLeft
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import ActivityResultDisplay, {
  getNormalizedNote,
  hasActivityResultDisplay,
  getTranslatedActivityResult
} from "./activity-result-display";
import { FontSizeTwelve } from "../common-styling";
import { ACTIVITY_KINDS, ACTIVITY_TYPES, DATE_TIME_FORMAT } from "../strings";
import RoundedIconContainer from "../rounded-icon-container";
import { generateDateTime } from "../../services/common-service";
import ResubmissionDetailsDisplay from "./resubmission-details-display";
import { useTranslation } from "react-i18next";

const activityIcons = {
  [ACTIVITY_TYPES.CALL]: faPhone,
  [ACTIVITY_TYPES.SMS]: faMessage,
  [ACTIVITY_TYPES.EMAIL]: faEnvelope,
  [ACTIVITY_TYPES.RESET]: faArrowRotateLeft,
  [ACTIVITY_TYPES.SYNC_MANUALLY]: faSync,
  [ACTIVITY_TYPES.PARTIAL_SYNC_MANUALLY]: faSync,
  [ACTIVITY_TYPES.DIGITAL_VERIFICATION]: faDesktopAlt,
  [ACTIVITY_TYPES.APPOINTMENT]: faCalendarCheck,
  [ACTIVITY_TYPES.LEAD_RESUBMISSION]: faRepeat,
  [ACTIVITY_TYPES.AI_LEADSERVICE_HANDOFF]: faRobot,
  [ACTIVITY_TYPES.AI_LEADSERVICE_MANUAL_TAKEOVER]: faRobot,
  [ACTIVITY_TYPES.MANUAL_LEAD_SERVICE_REMOVAL]: faDesktopAlt
};

const getActivityIcon = (activity) =>
  activity.display?.kind === ACTIVITY_KINDS.SYSTEM_ACTIVITY
    ? faDesktopAlt
    : activityIcons[activity.type];

const ActivityDivider = styled.div`
  width: 2px;
  height: 35px;
  margin-left: 18px;
  background: var(--activity-divider-color);
`;
const ActivityNote = styled.div`
  white-space: pre-line;
`;

const LeadActivities = ({ activities }) => {
  const { t } = useTranslation();
  return activities.length ? (
    activities.map((activity, index) => {
      const { _id, type, result, note, createdAt, serviceAgent } = activity;
      const normalizedNote = getNormalizedNote(note);

      return (
        <div key={_id || index} className="mb-2">
          <div className="d-flex align-items-center">
            <RoundedIconContainer
              iconColor="primary"
              borderColor="primary"
              icon={getActivityIcon(activity)}
            />
            <div className="flex-fill mx-3">
              {hasActivityResultDisplay(activity) ? (
                <ActivityResultDisplay
                  activity={activity}
                  note={normalizedNote}
                />
              ) : (
                <>
                  <OverlayTrigger
                    placement="left"
                    overlay={<Tooltip>{t("common.type")}</Tooltip>}
                  >
                    <div className="fw-bold">
                      {t(`leadActivities.type.${type}`)}
                    </div>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="left"
                    overlay={<Tooltip>{t("common.result")}</Tooltip>}
                  >
                    <div>{getTranslatedActivityResult(t, result)}</div>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <Tooltip>{t("leadActivities.noteTooltip")}</Tooltip>
                    }
                  >
                    <ActivityNote className="fw-bold">
                      {normalizedNote || "---"}
                    </ActivityNote>
                  </OverlayTrigger>
                  <FontSizeTwelve className="m-0 text-muted">
                    {generateDateTime(createdAt, DATE_TIME_FORMAT)}
                  </FontSizeTwelve>
                </>
              )}
              <ResubmissionDetailsDisplay
                details={activity.resubmissionDetails}
              />
            </div>
            {/* Agent Info */}
            {serviceAgent && (
              <div className="text-end">
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip>
                      {t("leadActivities.agentInfo")}:
                      <div>
                        {serviceAgent.firstName} {serviceAgent.lastName}
                      </div>
                    </Tooltip>
                  }
                >
                  <Button
                    size="sm"
                    as={Link}
                    target="_blank"
                    variant="outline-primary"
                    to={`/dashboard/user/${serviceAgent._id}`}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                </OverlayTrigger>
              </div>
            )}
          </div>
          {index !== activities.length - 1 && (
            <div className="mt-2">
              <ActivityDivider></ActivityDivider>
            </div>
          )}
        </div>
      );
    })
  ) : (
    <div className="py-2">{t("leadActivities.noActivityMsg")}</div>
  );
};
export default LeadActivities;
